import { useEffect, useRef, useState } from 'react';
import { makeStyles } from '@material-ui/core';
import classNames from 'classnames';
import { CategoryNode, pathOf, subtreeLeaves } from 'components/shared/UserCard/competenceTree';

export const ALL = null;
export const UNCATEGORIZED = '__uncategorized__';

/** Roughly how many category rows fit a laptop screen beside the list. */
const MAX_INITIAL_ROWS = 18;
export type Selection = string | null; // category id, UNCATEGORIZED, or ALL (null)

const useStyles = makeStyles((theme) => ({
  panel: {
    backgroundColor: 'white',
    borderRadius: 16,
    padding: '10px 8px',
  },
  row: {
    display: 'flex',
    alignItems: 'center',
    minHeight: 34,
    paddingRight: 4,
    borderRadius: 8,
    cursor: 'pointer',
    fontSize: 13.5,
    color: theme.palette.gray.g1,
    userSelect: 'none',
    WebkitTapHighlightColor: 'transparent',
    WebkitTouchCallout: 'none' as const, // no iOS long-press menu
    // Hover only where a pointer hovers; on tablets (which get the tree) a
    // hover would stick after a tap, so touch gets a pressed state and taller
    // rows instead.
    '@media (hover: hover)': {
      '&:hover': {
        backgroundColor: theme.palette.gray.g7,
      },
    },
    '&:active': {
      backgroundColor: theme.palette.gray.g7,
    },
    '@media (pointer: coarse)': {
      minHeight: 40,
    },
  },
  row_selected: {
    backgroundColor: '#E6EEF3 !important',
    color: '#38566B',
    fontWeight: 600,
  },
  caret: {
    width: 28, // tap target; the arrow itself stays small
    height: 28,
    flexShrink: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    border: 'none',
    background: 'none',
    padding: 0,
    color: theme.palette.gray.g2,
    cursor: 'pointer',
    WebkitTapHighlightColor: 'transparent',
    WebkitTouchCallout: 'none' as const, // no iOS long-press menu
    userSelect: 'none' as const,
    fontSize: 10,
    transition: 'transform 0.12s',
  },
  caret_open: {
    transform: 'rotate(90deg)',
  },
  caret_empty: {
    visibility: 'hidden',
  },
  name: {
    flex: 1,
    minWidth: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  count: {
    fontSize: 12,
    color: theme.palette.gray.g2,
    fontWeight: 400,
    padding: '0 6px',
    flexShrink: 0,
  },
  // Not a category, so it shouldn't read as one: muted italic, set apart.
  uncategorized: {
    fontStyle: 'italic',
    color: theme.palette.gray.g2,
  },
  divider: {
    height: 1,
    backgroundColor: theme.palette.gray.g6,
    margin: '8px 6px',
  },
  add: {
    display: 'block',
    width: '100%',
    minHeight: 34,
    padding: '0 10px',
    border: 'none',
    borderRadius: 8,
    background: 'none',
    textAlign: 'left',
    fontSize: 13.5,
    fontFamily: 'inherit',
    color: '#38566B',
    fontWeight: 500,
    cursor: 'pointer',
    WebkitTapHighlightColor: 'transparent',
    WebkitTouchCallout: 'none' as const, // no iOS long-press menu
    userSelect: 'none' as const,
    '@media (hover: hover)': {
      '&:hover': {
        backgroundColor: theme.palette.gray.g7,
      },
    },
    '&:active': {
      backgroundColor: theme.palette.gray.g7,
    },
  },
}));

/** Every category at any depth, collapsible, with the number of competencies
 *  underneath each. Starts fully expanded; the selected category's ancestors
 *  are always opened. */
const CategoryTree = ({
  tops,
  nodes,
  orphans,
  total,
  selected,
  onSelect,
  onAddCategory,
}: {
  tops: CategoryNode[];
  nodes: Map<string, CategoryNode>;
  orphans: number;
  total: number;
  selected: Selection;
  onSelect: (selection: Selection) => void;
  /** Admins only (or everyone where the firm allows it); opens Add category with no parent. */
  onAddCategory?: () => void;
}) => {
  const classes = useStyles();
  const [open, setOpen] = useState<Set<string>>(new Set());
  const initialized = useRef(false);

  // Start as expanded as fits: open categories level by level, top to bottom,
  // while the tree stays within about a laptop screen of rows. Small trees
  // open fully; big ones (Kvale) show their top levels with the rest a click
  // away. After that, expanding and collapsing is up to the user.
  useEffect(() => {
    if (initialized.current || nodes.size === 0) return;
    initialized.current = true;
    const expanded: string[] = [];
    let visibleRows = tops.length;
    let level = tops;
    while (level.length > 0) {
      const next: CategoryNode[] = [];
      for (const node of level) {
        if (node.children.length === 0) continue;
        if (visibleRows + node.children.length > MAX_INITIAL_ROWS) continue;
        visibleRows += node.children.length;
        expanded.push(node.id);
        next.push(...node.children);
      }
      level = next;
    }
    setOpen((prev) => new Set([...Array.from(prev), ...expanded]));
  }, [nodes, tops]);

  // Reveal the selection: open the selected category and every ancestor, so
  // picking a category also shows what's inside it.
  useEffect(() => {
    const node = selected ? nodes.get(selected) : undefined;
    if (!node) return;
    const ancestors = pathOf(node);
    if (ancestors.every((a) => open.has(a.id))) return;
    setOpen((prev) => new Set([...Array.from(prev), ...ancestors.map((a) => a.id)]));
  }, [selected, nodes]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggle = (id: string) =>
    setOpen((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const renderNode = (node: CategoryNode, depth: number) => {
    const hasChildren = node.children.length > 0;
    const isOpen = open.has(node.id);
    return (
      <div key={node.id}>
        <div
          className={classNames(classes.row, selected === node.id && classes.row_selected)}
          style={{ paddingLeft: 4 + depth * 16 }}
          onClick={() => onSelect(node.id)}
        >
          <button
            className={classNames(classes.caret, isOpen && classes.caret_open, !hasChildren && classes.caret_empty)}
            onClick={(e) => {
              e.stopPropagation();
              toggle(node.id);
            }}
            aria-label={isOpen ? 'Collapse' : 'Expand'}
          >
            ▶
          </button>
          <span className={classes.name} title={node.name}>
            {node.name}
          </span>
          <span className={classes.count}>{subtreeLeaves(node).length}</span>
        </div>
        {hasChildren && isOpen && node.children.map((child) => renderNode(child, depth + 1))}
      </div>
    );
  };

  return (
    <nav className={classes.panel}>
      <div
        className={classNames(classes.row, selected === ALL && classes.row_selected)}
        style={{ paddingLeft: 10 }}
        onClick={() => onSelect(ALL)}
      >
        <span className={classes.name}>All competencies</span>
        <span className={classes.count}>{total}</span>
      </div>
      {/* One divider between groups, never two in a row (a firm can have no
          categories, or no uncategorized competencies). */}
      {(tops.length > 0 || orphans > 0) && <div className={classes.divider} />}
      {tops.map((node) => renderNode(node, 0))}
      {orphans > 0 && (
        <>
          {tops.length > 0 && <div className={classes.divider} />}
          <div
            className={classNames(classes.row, selected === UNCATEGORIZED && classes.row_selected)}
            style={{ paddingLeft: 10 }}
            onClick={() => onSelect(UNCATEGORIZED)}
          >
            <span className={classNames(classes.name, selected !== UNCATEGORIZED && classes.uncategorized)}>
              Uncategorized
            </span>
            <span className={classes.count}>{orphans}</span>
          </div>
        </>
      )}
      {onAddCategory && (
        <>
          <div className={classes.divider} />
          <button className={classes.add} onClick={onAddCategory}>
            + Add category
          </button>
        </>
      )}
    </nav>
  );
};

export default CategoryTree;
